<template>
    <section class="bill-summary-section" :class="{ 'has-payments': displayTotalPaidLabel }">
        <TotalPaidLabel v-if="displayTotalPaidLabel" :totalPaid="bill.totalPaid" />

        <div class="bill-summary-content scrollable" ref="billItemsSection">
            <section class="no-order-yet-msg" v-if="displayEverythingIsPaidMsg">
                {{ allocation.allocation?.tableName }}: {{ trans_vue.bill.noOrderYet }}
            </section>
            <section class="unpaid-line-items-list" v-if="displayUnpaidItems" ref="unpaidItemsSection">
                <UnpaidLineItem
                    v-for="item in bill.unpaidItems"
                    :key="item.id"
                    :item="item"
                    :isSelectionMode="bill.isAnyItemSelected"
                    :quantityDigits="unpaidItemsQuantityDigits"
                    :disabled="!allocation.hasPaymentServices"
                    :showInternalNames="identity.hasOperatorAccess"
                    :indent="false"
                    :isUserWaiterOrAdmin="identity.hasOperatorAccess"
                    :forceDisplayGroupName="!identity.hasOperatorAccess"
                    @selectionChange="onItemSelectionChange"
                    @onClickOpenOrderDetails="(orderId: string) => onClickOrder(orderId)"
                />
            </section>

            <template v-if="displayPaidItems">
                <h3>{{ trans_vue.bill.fullyPaidItems }}</h3>
                <section class="paid-line-items-list">
                    <PaidLineItem
                        v-for="item in bill.paidItems"
                        :key="item.id"
                        :item="item"
                        :isUserWaiterOrAdmin="identity.hasOperatorAccess"
                        :showInternalNames="identity.hasOperatorAccess"
                        :quantityDigits="paidItemsQuantityDigits"
                        :indent="false"
                        :forceDisplayGroupName="!identity.hasOperatorAccess"
                        :onClickOpenOrderDetails="
                            (orderId: string) => {
                                onClickOrder(orderId);
                            }
                        "
                    />
                </section>
            </template>
        </div>

        <footer>
            <SelectAllButton :isShown="displaySelectAllButton" @click="selectAll" />
            <ClearSelectionButton :isShown="displayClearSelectionButton" @click="clearSelection" />
        </footer>
    </section>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { PullToRefresh } from "@/app/ui/PullToRefresh";
    import { Popup } from "@/components/dialog/Popup";
    import { Bill } from "@/data/model/Bill";
    import { UnpaidLineItem as UnpaidLineItemEntity } from "@/data/model/Bill";
    import { useBillStore } from "@/data/stores/bill";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { trans_vue } from "@/operator/localization/TransVue";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, nextTick, ref, watch } from "vue";

    import PaidLineItem from "@/components/bill/PaidLineItem.vue";
    import TotalPaidLabel from "@/components/bill/TotalPaidLabel.vue";
    import UnpaidLineItem from "@/components/bill/UnpaidLineItem.vue";
    import ClearSelectionButton from "@/components/elements/ClearSelectionButton.vue";
    import SelectAllButton from "@/components/elements/SelectAllButton.vue";

    // define stores
    const cart = useCartStore();
    const bill = useBillStore();
    const allocation = useTableAllocationStore();
    const identity = useIdentityStore();


    const emit = defineEmits<{
        onClickOrder: [orderId: string];
    }>();

    // define computed
    const unpaidItemsQuantityDigits = computed(() => Bill.getUnpaidMaxDisplayedDigits(bill.unpaidItems));
    const paidItemsQuantityDigits = computed(() => Bill.getPaidMaxDisplayedDigits(bill.paidItems));
    const isEverythingPaid = computed(() => !bill.hasUnpaidItems && !bill.hasPaidItems && !cart.hasItems);

    const displayTotalPaidLabel = computed(() => allocation.hasPaymentServices && bill.hasPaidItems);
    const displayUnpaidItems = computed(() => allocation.hasPaymentServices && bill.hasUnpaidItems);
    const displayPaidItems = computed(() => allocation.hasPaymentServices && bill.hasPaidItems);
    const displaySelectAllButton = computed(() => displayUnpaidItems.value && !bill.isAnyItemSelected);
    const displayClearSelectionButton = computed(() => displayUnpaidItems.value && bill.isAnyItemSelected);
    const displayEverythingIsPaidMsg = computed(() => allocation.hasPaymentServices && isEverythingPaid.value);

    // define methods

    function onClickOrder(orderId: string): void {
        emit("onClickOrder", orderId);
    }

    async function selectAll() {
        try {
            // await bill.selectAllItems();
            await TastierClient.context.selectAll();
            focusOnUnpaidItemsSection();
        } catch (ex) {
            handleInvalidSelection(ex);
        }
    }

    async function clearSelection() {
        try {
            // await bill.deselectAllItems();
            await TastierClient.context.selectItemsToPay([]);
            focusOnUnpaidItemsSection();
        } catch (ex) {
            handleInvalidSelection(ex);
        }
    }

    async function onItemSelectionChange(data: { item: UnpaidLineItemEntity; selectedQuantity: number }) {
        try {
            // await bill.selectItem(data.item, data.selectedQuantity);
            await bill.selectItem(data.item, data.selectedQuantity, false);
            await TastierClient.context.selectItemsToPay(bill.selectedItemsList);
        } catch (ex) {
            handleInvalidSelection(ex);
        }
    }

    function focusOnUnpaidItemsSection() {
        if (unpaidItemsSection.value) unpaidItemsSection.value.scrollIntoView({ behavior: "smooth" });
    }

    function handleInvalidSelection(e: unknown) {
        logError(e);

        Popup.show({
            uniqueId: "pmtconfirm",
            message: translations.pay.failedBillInvalidSelection,
            confirmButton: translations.general.ok,
            confirmHandler: async () => {
                await bill.loadCurrentBill();
            },
        });
    }

    // define template refs
    const unpaidItemsSection = ref<HTMLElement | null>(null);
    const billItemsSection = ref<HTMLElement | null>(null);

    watch(billItemsSection, async () => {
        if (billItemsSection.value) {
            await nextTick();
            new PullToRefresh($(billItemsSection.value!), () => TastierClient.context.reloadBill());
        }
    });
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    .bill-summary-section {
        position: absolute;
        top: 0;
        bottom: 104px;
        left: 0;
        right: 0;

        &.has-payments {
            > .scrollable {
                top: 27px;
            }

            > .ptrcont {
                margin-top: 27px;
            }
        }

        &:not(.ptr) {
            .ptrcont {
                display: none;
            }
        }

        > .total-paid {
            z-index: 209;
        }

        > .bill-summary-content {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            z-index: 200;
            transition: top 0.4s variables.$easeOutExpo;

            > .scrollable-content > section {
                padding-bottom: 1em;
            }
        }

        .unpaid-line-items-list {
            > :first-child {
                padding-top: 0.75em;
            }
        }

        h3 {
            margin-top: 1em;
            margin-bottom: 0.5em;
            padding: 0 1.666em;
            padding-top: 0.5em;
            padding-bottom: 0.5em;
            font-size: 14px;
            font-weight: 700;
        }

        .paid-line-items-list {
            padding-bottom: 3.4em !important;
        }

        .no-order-yet-msg {
            padding: 2em;
            text-align: center;
        }

        section + .no-payment-service-msg {
            padding-top: 0.75em;
        }

        .no-payment-service-msg {
            padding-top: 3.5em;
            width: 100%;
            text-align: center;
        }

        > footer {
            position: absolute;
            bottom: 0;
            left: 50%;
            z-index: 210;
        }
    }
</style>
